import { createTheme, ThemeOptions, PaletteOptions, SimplePaletteColorOptions, PaletteMode } from "@mui/material/styles";


interface DefaultSimplePaletteColorOptions extends SimplePaletteColorOptions {
    main: string;
    dark: string;
}
interface DefaultPaletteOptions extends PaletteOptions {
    primary: DefaultSimplePaletteColorOptions,
    secondary: DefaultSimplePaletteColorOptions,
}

interface DefaultThemeOptions extends ThemeOptions {
    palette: DefaultPaletteOptions;
}

const lightPalette: DefaultPaletteOptions = {
    mode: 'light',
    primary: {
        main: '#be1622',
        dark: '#740009',
    },
    secondary: {
        main: '#0E796E',
        dark: '#004A42',
    }   
};

const darkPalette: DefaultPaletteOptions = {
    mode: 'dark',
    primary: {
        main: '#ef5350',
        dark: '#c62828',
    },
    secondary: {
        main: '#4db6ac',
        dark: '#00796b',
    },
    background: {
        default: '#121212',
        paper: '#1e1e1e',
    },
    text: {
        primary: '#e0e0e0',
        secondary: '#b0b0b0',
    },
};

export const createAppTheme = (mode: PaletteMode = 'light'): DefaultThemeOptions => {
    const palette = mode === 'dark' ? darkPalette : lightPalette;
    
    return createTheme({
        palette,
    typography: {
        fontFamily: 'sans-serif', // Material UI uses the Roboto font by default
        // Base font size - reduced from default 14px to 12px for overall smaller text
        fontSize: 14,
        // Override all typography variants with smaller responsive sizes
        /*
        h1: {
            fontSize: '1.75rem', // Reduced from default ~2.125rem
            '@media (min-width:600px)': {
                fontSize: '2rem',
            },
            '@media (min-width:900px)': {
                fontSize: '2.25rem',
            },
        },
        h2: {
            fontSize: '1.5rem', // Reduced from default ~1.5rem
            '@media (min-width:600px)': {
                fontSize: '1.75rem',
            },
            '@media (min-width:900px)': {
                fontSize: '2rem',
            },
        },
        h3: {
            fontSize: '1.25rem', // Reduced from default ~1.25rem
            '@media (min-width:600px)': {
                fontSize: '1.375rem',
            },
            '@media (min-width:900px)': {
                fontSize: '1.5rem',
            },
        },
        h4: {
            fontSize: '1.125rem', // Reduced from default ~1.125rem
            '@media (min-width:600px)': {
                fontSize: '1.25rem',
            },
            '@media (min-width:900px)': {
                fontSize: '1.375rem',
            },
        },
        h5: {
            fontSize: '1rem', // Reduced from default ~1rem
            '@media (min-width:600px)': {
                fontSize: '1.125rem',
            },
            '@media (min-width:900px)': {
                fontSize: '1.25rem',
            },
        },
        h6: {
            fontSize: '0.875rem', // Reduced from default ~0.875rem
            '@media (min-width:600px)': {
                fontSize: '1rem',
            },
            '@media (min-width:900px)': {
                fontSize: '1.125rem',
            },
        },
        subtitle1: {
            fontSize: '0.875rem', // Reduced from default ~1rem
            '@media (min-width:600px)': {
                fontSize: '0.9375rem',
            },
            '@media (min-width:900px)': {
                fontSize: '1rem',
            },
        },
        subtitle2: {
            fontSize: '0.4rem', // Reduced from default ~0.875rem
            '@media (min-width:600px)': {
                fontSize: '0.84375rem',
            },
            '@media (min-width:900px)': {
                fontSize: '0.875rem',
            },
        },
        body1: {
            fontSize: '0.8125rem', // Reduced from default ~1rem
            '@media (min-width:600px)': {
                fontSize: '0.875rem',
            },
            '@media (min-width:900px)': {
                fontSize: '0.9375rem',
            },
        },
        body2: {
            fontSize: '0.75rem', // Reduced from default ~0.875rem
            '@media (min-width:600px)': {
                fontSize: '0.8125rem',
            },
            '@media (min-width:900px)': {
                fontSize: '0.875rem',
            },
        },
        button: {
            fontSize: '0.75rem', // Reduced from default ~0.875rem
            '@media (min-width:600px)': {
                fontSize: '0.8125rem',
            },
            '@media (min-width:900px)': {
                fontSize: '0.875rem',
            },
        },
        caption: {
            fontSize: '0.6875rem', // Reduced from default ~0.75rem
            '@media (min-width:600px)': {
                fontSize: '0.71875rem',
            },
            '@media (min-width:900px)': {
                fontSize: '0.75rem',
            },
        },
        overline: {
            fontSize: '0.6875rem', // Reduced from default ~0.75rem
            '@media (min-width:600px)': {
                fontSize: '0.71875rem',
            },
            '@media (min-width:900px)': {
                fontSize: '0.75rem',
            },
        },
        */
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: mode === 'dark' ? {
                body: {
                    backgroundColor: '#121212',
                    color: '#ffffff',
                }
            } : undefined,
        },
        MuiPaper: {
            styleOverrides: {
                root: mode === 'dark' ? {
                    backgroundImage: 'none',
                    backgroundColor: '#1e1e1e',
                } : undefined,
            },
        },
        MuiAppBar: {
            defaultProps: {
                style: {
                    // Keep existing styles
                }
            },
            styleOverrides: {
                root: mode === 'dark' ? {
                    backgroundColor: '#1e1e1e',
                } : undefined,
            },
        },
        MuiDrawer: {
            styleOverrides: {
                paper: mode === 'dark' ? {
                    backgroundColor: '#2d2d2d',
                } : undefined,
            },
        },
        MuiTab: {
            styleOverrides: {
                root: mode === 'dark' ? {
                    '&.Mui-selected': {
                        color: '#4db6ac', // Keep secondary color visible in dark mode
                        fontWeight: 600,
                    },
                    // For tabs with textColor="secondary"
                    '&.MuiTab-textColorSecondary': {
                        color: '#26a69a',
                        '&.Mui-selected': {
                            color: '#4db6ac',
                        },
                    },
                } : undefined,
            },
        },
        MuiContainer: {
            defaultProps: {
                style: {
                    fontSize: '0.75rem' // Reduced from 0.8rem
                }
            }
        },
        MuiToolbar: {
            defaultProps: {
                style: {
                    // Keep existing styles
                }
            },
            styleOverrides: {
                root: mode === 'dark' ? {
                    // Let it inherit color from theme palette
                } : undefined,
            },
        },
        MuiGrid: {
            defaultProps: {
                // Removed fontSize override - will inherit from typography
            }
        },
        MuiTypography: {
            defaultProps: {
                // Removed fontSize override - will use the typography variants defined above
            },
            styleOverrides: {
                root: {
                    // Ensure responsive behavior is applied consistently
                    lineHeight: 1.4, // Slightly reduced line height for denser text
                },
            },
        },
        // Additional components that might benefit from smaller text
        MuiButton: {
            styleOverrides: {
                root: {
                    fontSize: '0.6rem',
                    '@media (min-width:600px)': {
                        fontSize: '0.7rem',
                    },
                    '@media (min-width:900px)': {
                        fontSize: '0.8rem',
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: '0.6rem',
                    '@media (min-width:600px)': {
                        fontSize: '0.7rem',
                    },
                    '@media (min-width:900px)': {
                        fontSize: '0.8rem',
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize: '0.6rem',
                    '@media (min-width:600px)': {
                        fontSize: '0.7rem',
                    },
                    '@media (min-width:900px)': {
                        fontSize: '0.8rem',
                    },
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    fontSize: '0.6rem',
                    '@media (min-width:600px)': {
                        fontSize: '0.7rem',
                    },
                    '@media (min-width:900px)': {
                        fontSize: '0.8rem',
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    fontSize: '0.6rem',
                    '@media (min-width:600px)': {
                        fontSize: '0.7rem',
                    },
                    '@media (min-width:900px)': {
                        fontSize: '0.8rem',
                    },
                },
            },
        },
    },
});
};

// Default export for backward compatibility
const themeOptions: DefaultThemeOptions = createAppTheme('light');

export default themeOptions;

